import React from 'react';
import Head from 'next/head';
import { observer, Observer } from 'mobx-react';
import { BeakerIcon, GlobeAltIcon, LightningBoltIcon, ScaleIcon } from '@heroicons/react/outline';
import { runInAction, observable, makeObservable } from 'mobx';
import CountUp from 'react-countup';
import 'tailwindcss/tailwind.css';
import { memoize } from '../../utils/mobx/decorators/memoize';
import { Store } from '../../pagepart/store/Store';
import { StoreContext } from 'src/pagepart/utils';
import { Env } from '../../env/Env';
import { ServerProps } from '../../pagepart/portal/utils';
import { InfoTopbar } from '../../tailwind/info/InfoTopbar';
import { prismaService } from '../../server/service/PrismaService';
import { mysql } from '../../server';
import { number } from 'prop-types';
import { ax } from '../../classes/runtime/axios/axios';

function numberWithSpaces(x) {
  if (!x) return '';
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

const features = [
  {
    name: 'Nutzbar für alle',
    description: 'Schon die kostenfreie Version bietet ein umfangreiches Lernerlebnis.',
    icon: GlobeAltIcon,
  },
  {
    name: 'Spaß beim Lernen',
    description: 'Die Fragen sind kurz und knapp, oft spielerisch. Nach dem Lerndurchgang wartet eine Belohnung auf dich!',
    icon: LightningBoltIcon,
  },
  {
    name: 'Echte Prüfungsfragen',
    description: 'Die meisten unserer Fragen basieren auf echten Prüfungsfragen aus Klassenarbeiten und Tests.',
    icon: ScaleIcon,
  },
  {
    name: 'Innovative Technik',
    description: 'Wir verbessern Schlaukopf ständig und setzen brandneue Techniken ein, z.B. Spracheingabe.',
    icon: BeakerIcon,
  },
];

const people = [
  /*{
    name: 'Michael Hicke',
    role: 'Programmierung',
    imageUrl: '/images/michaelhicke.png',
  },
  {
    name: 'Dr. med. Agathe Hicke',
    role: 'Inhaltserstellung',
    imageUrl: '/images/agathehicke.png',
  },*/
  // More people...
];

export async function getServerSideProps(rawContext) {
  const { env, userEnv, requestEnv } = await Env.forNextContext(rawContext);

  const sql_answers = `Select count(*) as questions, sum(q.count) as answers, sum(q.floatReached) as reached, sum(q.floatReachable) as max From question q WHERE q.accepted = '1'`;

  const answerData_fixed = await mysql.mysqlLocal.sql2Row(sql_answers);
  const numberOfAnswers_fixed: number = parseInt(answerData_fixed.answers);

  const sql_questions = `Select count(*) as num From question q Where q.accepted = '1'`;

  const questionData = await mysql.mysqlLocal.sql2Row(sql_questions);
  const numberOfQuestionsInPool: number = questionData.num;

  const [unprocessedAnswers_server0, unprocessedAnswers_server1] = await Promise.all([
    prismaService.db0.answer.count({ where: { flag: 0 } }),
    prismaService.db1.answer.count({ where: { flag: 0 } }),
  ]);

  const numberOfAnswers_unprocessed = unprocessedAnswers_server0 + unprocessedAnswers_server1;

  return {
    props: {
      numberOfAnswers_unprocessed,
      numberOfAnswers_fixed,
      numberOfQuestionsInPool,
      ...env.transfer,
    },
  };
}

@observer
class InfoProjekt extends React.Component<ServerProps<typeof getServerSideProps>> {
  @observable unprocessedAnswerCount: number = 0;

  constructor(props) {
    super(props);
    makeObservable(this);
  }

  @memoize
  get store() {
    return Store.forApp(this);
  }

  setupPage(store: Store) {
    store.portalStore.metaTitle = 'Schlaukopf Projekt';
  }

  get totalAnswerCount() {
    return this.props.numberOfAnswers_fixed + this.unprocessedAnswerCount;
  }

  render() {
    const { userData, numberOfAnswers_fixed, numberOfQuestionsInPool } = this.props;

    return (
      <StoreContext.Provider value={this.store}>
        <Head>
          <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
          <title>Schlaukopf - Das Projekt</title>
          <meta name="Description" content="Informationen über das Lernrprogramm Schlaukopf." />
          <meta name="Keywords" content="Schlaukopf,Schlaukopf.de,Lernprogramm" />
          <meta name="Language" content="de" />
          <meta name="robots" content="index,follow" />
        </Head>
        <div className="antialiased font-sans  bg-gray-50" style={{ minHeight: '100vh' }}>
          <InfoTopbar />

          <div>
            <main>
              <div className="bg-white">
                <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
                  <div className="text-center">
                    <h2 className="text-base font-semibold text-indigo-600 tracking-wide uppercase">Schlaukopf ist</h2>
                    <p className="mt-1 text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
                      Eine der beliebtesten Lernapps
                    </p>
                    <p className="max-w-xl mt-5 mx-auto text-xl text-gray-500">im deutschsprachigen Raum.</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 overflow-hidden">
                <div className="relative max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
                  <svg
                    className="absolute top-0 left-full transform -translate-x-1/2 -translate-y-3/4 lg:left-auto lg:right-full lg:translate-x-2/3 lg:translate-y-1/4"
                    width={404}
                    height={784}
                    fill="none"
                    viewBox="0 0 404 784"
                    aria-hidden="true"
                  >
                    <defs>
                      <pattern id="8b1b5f72-e944-4457-af67-0c6d15a99f38" x={0} y={0} width={20} height={20} patternUnits="userSpaceOnUse">
                        <rect x={0} y={0} width={4} height={4} className="text-gray-200" fill="currentColor" />
                      </pattern>
                    </defs>
                    <rect width={404} height={784} fill="url(#8b1b5f72-e944-4457-af67-0c6d15a99f38)" />
                  </svg>

                  <div className="relative lg:grid lg:grid-cols-3 lg:gap-x-8">
                    <div className="lg:col-span-1">
                      <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">Unsere Mission.</h2>
                    </div>
                    <dl className="mt-10 space-y-10 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-x-8 sm:gap-y-10 lg:mt-0 lg:col-span-2">
                      {features.map(feature => (
                        <div key={feature.name}>
                          <dt>
                            <div className="flex items-center justify-center h-12 w-12 rounded-md bg-indigo-500 text-white">
                              <feature.icon className="h-6 w-6" aria-hidden="true" />
                            </div>
                            <p className="mt-5 text-lg leading-6 font-medium text-gray-900">{feature.name}</p>
                          </dt>
                          <dd className="mt-2 text-base text-gray-500">{feature.description}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 pt-12 sm:pt-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">Danke!</h2>
                    <p className="mt-3 text-xl text-gray-500 sm:mt-4">für Eure intensive Nutzung und positiven Rückmeldungen</p>
                  </div>
                </div>
                <div className="mt-10 pb-12 bg-white sm:pb-16">
                  <div className="relative">
                    <div className="absolute inset-0 h-1/2 bg-gray-50" />
                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                      <div className="max-w-4xl mx-auto">
                        <dl className="rounded-lg bg-white shadow-lg sm:grid sm:grid-cols-2">
                          <div className="flex flex-col border-b border-gray-100 p-6 text-center sm:border-0 sm:border-r">
                            <dt className="order-2 mt-2 text-lg leading-6 font-medium text-gray-500">Fragen in der Sammlung</dt>
                            <dd className="order-1 text-5xl font-extrabold text-indigo-600">{numberWithSpaces(numberOfQuestionsInPool)}</dd>
                          </div>
                          <div className="flex flex-col border-t border-b border-gray-100 p-6 text-center sm:border-0 sm:border-l sm:border-r">
                            <dt className="order-2 mt-2 text-lg leading-6 font-medium text-gray-500">Antworten seit dem Jahr 2007</dt>
                            <dd className="order-1 text-5xl font-extrabold text-indigo-600">
                              {this.unprocessedAnswerCount > 0 && (
                                <Observer>
                                  {() => (
                                    <CountUp
                                      end={this.totalAnswerCount}
                                      preserveValue={true}
                                      duration={4}
                                      separator=" "
                                      useEasing={false}
                                    />
                                  )}
                                </Observer>
                              )}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white pt-16 lg:py-24">
                <div className="pb-16 bg-indigo-600 lg:pb-0 lg:z-10 lg:relative">
                  <div className="lg:mx-auto lg:max-w-7xl lg:px-8 lg:grid lg:grid-cols-3 lg:gap-8">
                    <div className="relative lg:-my-8">
                      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-1/2 bg-white lg:hidden" />
                      <div className="mx-auto max-w-md px-4 sm:max-w-3xl sm:px-6 lg:p-0 lg:h-full">
                        <div className="aspect-w-10 aspect-h-6 rounded-xl shadow-xl overflow-hidden sm:aspect-w-16 sm:aspect-h-7 lg:aspect-none lg:h-full">
                          <img className="object-cover lg:h-full lg:w-full" src="/static/img/info/technik.jpg" alt="" />
                        </div>
                      </div>
                    </div>
                    <div className="mt-12 lg:m-0 lg:col-span-2 lg:pl-8">
                      <div className="mx-auto max-w-md px-4 sm:max-w-2xl sm:px-6 lg:px-0 lg:py-20 lg:max-w-none">
                        <blockquote>
                          <div>
                            <svg className="h-12 w-12 text-white opacity-25" fill="currentColor" viewBox="0 0 32 32" aria-hidden="true">
                              <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                            </svg>
                            <p className="mt-6 text-2xl font-medium text-white">
                              Gutes E-Learning passt sich dem Lernenden an – nicht umgekehrt.
                            </p>
                          </div>
                          <footer className="mt-6">

                          </footer>
                        </blockquote>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white">
                <div className="mx-auto py-12 px-4 max-w-7xl sm:px-6 lg:px-8 lg:py-24">
                  <div className="grid grid-cols-1 gap-12 lg:grid-cols-3 lg:gap-8">
                    <div className="lg:col-span-2">
                      <ul className="space-y-12 sm:grid sm:grid-cols-2 sm:gap-12 sm:space-y-0 lg:gap-x-8">
                        {people.map(person => (
                          <li key={person.name}>
                            <div className="flex items-center space-x-4 lg:space-x-6">
                              <img className="w-16 h-16 rounded-full lg:w-20 lg:h-20" src={person.imageUrl} alt="" />
                              <div className="font-medium text-lg leading-6 space-y-1">
                                <h3>{person.name}</h3>
                                <p className="text-indigo-600">{person.role}</p>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </StoreContext.Provider>
    );
  }

  componentDidMount() {
    this.unprocessedAnswerCount = this.props.numberOfAnswers_unprocessed;
    this.updateUnprocessedAnswers();
    setInterval(() => {
      this.updateUnprocessedAnswers();
    }, 4000);
  }

  async updateUnprocessedAnswers() {
    const response = await ax.get(`/api/info/unprocessedAnswerCount?time=${new Date().getTime()}`);
    runInAction(() => {
      this.unprocessedAnswerCount = response.data.payload;
    });
  }
}

export default InfoProjekt;
